// Central site configuration — all editable business details live here.
// The admin page reads/writes to localStorage, falling back to these defaults.

export interface BusinessConfig {
  name: string;
  phone: string;
  phoneHref: string;
  email: string;
  address: string;
  addressLine2: string;
  entrance: string;
  postcode: string;
  vtsNumber: string;
  instagram: string;
  instagramUrl: string;
  linktreeUrl: string;
  googleReviewsUrl: string;
  qrImagePath: string;
  googleMapsUrl: string;
  googleMapsEmbedUrl: string;
  whatsappUrl: string;
  hours: { day: string; time: string }[];
  launchChecklist: { id: string; label: string; confirmed: boolean }[];
}

export interface ServiceConfig {
  slug: string;
  name: string;
  shortDesc: string;
  icon: string;
  headline: string;
  intro: string;
  included: string[];
  commonIssues: string[];
  signs: string[];
  whyUs: string[];
  process: { step: string; desc: string }[];
  faqs: { q: string; a: string }[];
}

export interface ReviewConfig {
  name: string;
  text: string;
  rating: number;
  service: string;
  /** Where the review came from, e.g. "Google", "Trustpilot". Defaults to "Google". */
  source?: string;
  /** Public URL of the original review for schema.org compliance + trust. */
  sourceUrl?: string;
  /** Page slugs this review should appear on. "all" (default) shows everywhere.
   *  Known slugs: "home", "mot", "servicing", "diagnostics", "mechanical-repairs",
   *  "bodywork", "areas", "services". */
  pages?: string[];
}

export interface AreaConfig {
  slug: string;
  name: string;
  description: string;
}

export const defaultBusiness: BusinessConfig = {
  name: "Woodseer Autos",
  phone: "020 7537 2447",
  phoneHref: "tel:+442075372447",
  email: "info@woodseerautos.co.uk",
  address: "16 Pixley Street",
  addressLine2: "Poplar, London",
  entrance: "Yard entrance via 14 Copenhagen Place, London E14 7DF — pull through the gated yard into the workshop car park. Pixley Street is the registered address; sat-nav to Copenhagen Place.",
  postcode: "E14 7DF",
  vtsNumber: "V106376",
  instagram: "@woodseerautos",
  instagramUrl: "https://www.instagram.com/woodseerautos/",
  linktreeUrl: "https://linktr.ee/woodseerautos",
  googleReviewsUrl: "https://share.google/ex6hlV5y3QnHwzg2P",
  qrImagePath: "/images/qr-linktree.png",
  googleMapsUrl: "https://www.google.com/maps/dir/?api=1&destination=14+Copenhagen+Place+London+E14+7DF&destination_place_id=&travelmode=driving",
  googleMapsEmbedUrl: "https://www.google.com/maps?q=14+Copenhagen+Place+London+E14+7DF&output=embed",
  whatsappUrl: "https://wa.me/442075372447",
  hours: [
    { day: "Monday – Saturday", time: "9:00 – 18:00" },
    { day: "Sunday", time: "Closed" },
  ],
  launchChecklist: [
    { id: "address", label: "Confirm exact address", confirmed: true },
    { id: "postcode", label: "Confirm final postcode", confirmed: true },
    { id: "hours", label: "Confirm opening hours", confirmed: true },
    { id: "mot-classes", label: "Confirm MOT classes/services offered", confirmed: true },
    { id: "booking", label: "Confirm online booking / WhatsApp / collection availability", confirmed: false },
  ],
};

export const services: ServiceConfig[] = [
  {
    slug: "mot",
    name: "MOT Testing",
    shortDesc: "Government-standard MOT testing for cars and light commercial vehicles. Fast, thorough and hassle-free.",
    icon: "ClipboardCheck",
    headline: "MOT Testing in East London",
    intro: "Keep your vehicle road-legal with a thorough MOT test at Woodseer Autos. We're a fully authorised MOT testing station serving drivers across East London, from Poplar and Canary Wharf to Limehouse and beyond. Our experienced technicians carry out every test to the highest standard — no shortcuts, no hidden charges.",
    included: [
      "Full government-standard MOT inspection",
      "Lights, brakes, steering and suspension checks",
      "Emissions testing",
      "Tyre condition and tread depth assessment",
      "Clear pass/fail report with advisory notes",
      "Free retest if repairs are done with us",
    ],
    commonIssues: [
      "Worn brake pads or discs",
      "Faulty or misaligned headlights",
      "Damaged or worn tyres",
      "Exhaust emissions failures",
      "Windscreen chips in the driver's line of sight",
      "Suspension wear or damaged bushings",
    ],
    signs: [
      "Your MOT is due within the next month",
      "Dashboard warning lights are on",
      "You've noticed unusual noises or vibrations",
      "Your tyres look worn or uneven",
      "You're buying or selling a vehicle",
    ],
    whyUs: [
      "Authorised MOT testing station",
      "Honest results — we won't fail you on things that don't need failing",
      "Fast turnaround — most tests done within the hour",
      "If repairs are needed, we can handle them on-site",
      "Competitive pricing with no hidden fees",
      "Trusted by local drivers across East London",
    ],
    process: [
      { step: "Book In", desc: "Call us or drop in to arrange your MOT at a time that suits you." },
      { step: "We Test", desc: "Our technicians carry out a full MOT inspection to government standards." },
      { step: "Get Results", desc: "You'll get a clear pass/fail result with any advisories explained in plain English." },
      { step: "Repairs if Needed", desc: "If anything needs fixing, we'll quote fairly and get you back on the road fast." },
    ],
    faqs: [
      { q: "How long does an MOT take?", a: "Most MOTs are completed within 45 minutes to an hour. If we're quiet, it can be even faster." },
      { q: "Can I wait while my MOT is done?", a: "Yes, you're welcome to wait at the garage. We'll keep you updated throughout." },
      { q: "What happens if my car fails?", a: "We'll explain exactly what failed and why, give you an honest quote for repairs, and offer a free retest once the work is done." },
      { q: "When should I book my MOT?", a: "You can book your MOT up to a month before it expires without losing any days from your current certificate." },
      { q: "Do you test vans and commercial vehicles?", a: "Yes, we test cars and light commercial vehicles. Call us to confirm your vehicle class." },
    ],
  },
  {
    slug: "servicing",
    name: "Car Servicing",
    shortDesc: "Interim, full and major servicing to keep your vehicle running smoothly and maintain its value.",
    icon: "Settings",
    headline: "Car Servicing in East London",
    intro: "Regular servicing keeps your car reliable, safe and holding its value. At Woodseer Autos, we offer interim, full and major services tailored to your vehicle's needs. Whether you drive a daily commuter car, a family SUV or a work van, we'll keep it running at its best.",
    included: [
      "Oil and filter change",
      "Brake inspection",
      "Fluid level checks and top-ups",
      "Tyre condition and pressure check",
      "Battery health check",
      "Air filter and cabin filter inspection",
      "Full vehicle health report",
    ],
    commonIssues: [
      "Overdue oil changes causing engine wear",
      "Worn brake components",
      "Low or contaminated coolant",
      "Clogged air filters reducing performance",
      "Ageing spark plugs causing misfires",
      "Degraded transmission fluid",
    ],
    signs: [
      "Your service light is on",
      "It's been over 12 months or 12,000 miles since your last service",
      "You've noticed reduced fuel economy",
      "The engine feels sluggish or rough",
      "You're hearing new or unusual noises",
    ],
    whyUs: [
      "We service all makes and models",
      "Genuine and OE-quality parts used",
      "Clear, itemised pricing — no surprises",
      "Service book stamped to protect your warranty",
      "Experienced technicians who take pride in their work",
      "Convenient location in East London",
    ],
    process: [
      { step: "Book Your Service", desc: "Call us to arrange a date and time. We'll confirm what level of service your car needs." },
      { step: "Drop Off", desc: "Bring your car in. We'll get started promptly." },
      { step: "Thorough Service", desc: "We carry out every check on the service schedule, plus a visual health check of the whole vehicle." },
      { step: "Collect", desc: "We'll call you when it's ready. You'll get a full report and your service book stamped." },
    ],
    faqs: [
      { q: "How often should I service my car?", a: "Most manufacturers recommend a service every 12 months or 12,000 miles, whichever comes first. Check your handbook or ask us." },
      { q: "Will servicing with you void my warranty?", a: "No. Under UK law, you can have your car serviced at any independent garage without affecting your manufacturer warranty, as long as the correct parts and procedures are used." },
      { q: "What's the difference between an interim and full service?", a: "An interim service covers the essentials like oil, filters and brakes. A full service is more comprehensive, covering additional checks and replacements. We'll advise which is right for you." },
      { q: "Do you service all makes and models?", a: "Yes, we service all makes and models including European, Japanese, Korean and American vehicles." },
    ],
  },
  {
    slug: "diagnostics",
    name: "Diagnostics",
    shortDesc: "Advanced engine and vehicle diagnostics to find faults fast and fix them right first time.",
    icon: "MonitorCog",
    headline: "Car Diagnostics in East London",
    intro: "Modern vehicles rely on complex electronic systems. When something goes wrong, a warning light or unusual behaviour can point to dozens of possible causes. At Woodseer Autos, we use professional-grade diagnostic equipment to pinpoint the exact issue — saving you time and money on unnecessary guesswork.",
    included: [
      "Full OBD-II diagnostic scan",
      "Engine management fault code reading",
      "ABS, airbag and traction control diagnostics",
      "Live data analysis",
      "Clear explanation of findings",
      "Recommended repair plan with honest pricing",
    ],
    commonIssues: [
      "Engine management warning light",
      "ABS or traction control warnings",
      "Airbag light staying on",
      "Poor fuel economy",
      "Rough idling or misfires",
      "Intermittent electrical faults",
    ],
    signs: [
      "A warning light has appeared on your dashboard",
      "Your car feels different — less power, rough running, poor economy",
      "You've been told there's a fault code but want a second opinion",
      "An intermittent problem keeps coming back",
    ],
    whyUs: [
      "Professional-grade diagnostic tools covering all major manufacturers",
      "Experienced technicians who understand what the codes actually mean",
      "We diagnose and repair — no need to go elsewhere",
      "Honest assessment — we won't recommend work you don't need",
      "Fast turnaround on most diagnostic checks",
    ],
    process: [
      { step: "Describe the Issue", desc: "Tell us what's happening — warning lights, noises, performance changes." },
      { step: "Diagnostic Scan", desc: "We connect professional equipment to your vehicle's systems and read the data." },
      { step: "Analysis", desc: "Our technicians interpret the results and identify the root cause." },
      { step: "Report & Repair", desc: "We explain the findings clearly and quote for any repairs needed." },
    ],
    faqs: [
      { q: "How long does a diagnostic check take?", a: "A standard diagnostic scan takes around 30 minutes. More complex issues may take longer if live testing is needed." },
      { q: "Is a diagnostic scan the same as an MOT?", a: "No. A diagnostic scan reads electronic fault codes from your vehicle's computer systems. An MOT is a visual and mechanical safety inspection. They check different things." },
      { q: "Can you clear warning lights?", a: "Yes, but only after we've identified and resolved the underlying issue. Clearing a light without fixing the fault just means it'll come back." },
      { q: "Do you work on all car brands?", a: "Yes, our diagnostic equipment covers all major manufacturers including BMW, Mercedes, Audi, VW, Ford, Vauxhall, Toyota, Honda and more." },
    ],
  },
  {
    slug: "mechanical-repairs",
    name: "Mechanical Repairs",
    shortDesc: "Expert repairs for brakes, clutch, gearbox, suspension and all mechanical components.",
    icon: "Wrench",
    headline: "Mechanical Repairs in East London",
    intro: "From brake replacements and clutch repairs to suspension work and engine overhauls, Woodseer Autos handles all types of mechanical repair. Our experienced team works on all makes and models, using quality parts and honest pricing to get you back on the road safely.",
    included: [
      "Brake pads, discs and caliper repairs",
      "Clutch replacement and repair",
      "Gearbox diagnostics and repair",
      "Suspension — springs, shock absorbers, bushings",
      "Steering rack and power steering repairs",
      "Engine repairs and component replacement",
      "Exhaust system repairs",
      "Cooling system and radiator work",
    ],
    commonIssues: [
      "Squealing or grinding brakes",
      "Clutch slipping or biting point changing",
      "Gearbox crunching or difficulty selecting gears",
      "Suspension knocking over bumps",
      "Steering pulling to one side",
      "Overheating engine",
    ],
    signs: [
      "Unusual noises — grinding, knocking, squealing",
      "Vibrations through the steering wheel or pedals",
      "Warning lights on the dashboard",
      "Reduced braking performance",
      "Difficulty changing gear",
      "Fluid leaks under the vehicle",
    ],
    whyUs: [
      "Experienced mechanics who've seen it all",
      "We work on all makes and models",
      "Quality OE-equivalent parts as standard",
      "Clear quotes before we start work",
      "No unnecessary upselling",
      "Fast turnaround — we respect your time",
    ],
    process: [
      { step: "Tell Us What's Wrong", desc: "Describe the issue or let us inspect the vehicle." },
      { step: "Honest Assessment", desc: "We diagnose the fault and explain what needs doing." },
      { step: "Fair Quote", desc: "You get a clear price before any work starts." },
      { step: "Expert Repair", desc: "We fix it properly, test it thoroughly and hand it back ready to drive." },
    ],
    faqs: [
      { q: "Can you fix any make or model?", a: "Yes, we repair all makes and models. If we need a specialist part, we'll source it and let you know the timeline." },
      { q: "How long do repairs usually take?", a: "It depends on the job. Brake pads might take an hour; a clutch replacement could be a day. We'll always give you a realistic timeframe upfront." },
      { q: "Do you offer a warranty on repairs?", a: "Yes, all our repairs come with a parts and labour warranty. Ask us for details when you book in." },
      { q: "Can I get a quote before committing?", a: "Absolutely. We'll assess the vehicle and give you a clear, written quote before starting any work." },
    ],
  },
  {
    slug: "bodywork",
    name: "Bodywork",
    shortDesc: "Dent repair, paint correction, scratch removal and collision damage repair to restore your vehicle.",
    icon: "Paintbrush",
    headline: "Bodywork & Paint Repair in East London",
    intro: "Scratches, dents, scuffs and collision damage don't just look bad — they can affect your vehicle's value and protection against rust. At Woodseer Autos, we offer professional bodywork repairs that restore your car to its best, from minor cosmetic touch-ups to more serious structural work.",
    included: [
      "Dent removal and panel repair",
      "Scratch and scuff repair",
      "Paint touch-up and respray",
      "Bumper repair and replacement",
      "Collision damage assessment and repair",
      "Rust treatment and prevention",
      "Colour matching",
    ],
    commonIssues: [
      "Car park dents and dings",
      "Scratches from minor bumps",
      "Bumper scuffs and cracks",
      "Rust spots developing",
      "Faded or peeling paintwork",
      "Collision or accident damage",
    ],
    signs: [
      "Visible dents, scratches or scuffs on your bodywork",
      "Paint peeling or bubbling — could indicate rust underneath",
      "You've been in a minor collision or scrape",
      "Your bumper is cracked or misaligned",
      "You want to restore your car's appearance before selling",
    ],
    whyUs: [
      "Skilled bodywork technicians",
      "Professional colour matching for seamless results",
      "We handle everything from small dents to full panel repairs",
      "Honest assessment — we'll tell you what's worth fixing and what isn't",
      "Competitive pricing for high-quality work",
    ],
    process: [
      { step: "Assessment", desc: "Bring the vehicle in or send photos. We'll assess the damage and give you options." },
      { step: "Quote", desc: "You'll get a clear, itemised quote with no hidden costs." },
      { step: "Repair", desc: "Our team carries out the bodywork to a high standard." },
      { step: "Finish", desc: "Final inspection, quality check and handover. Your car looks right again." },
    ],
    faqs: [
      { q: "How much does bodywork repair cost?", a: "It depends on the extent of the damage. Small dents and scratches start from a reasonable price — we'll always quote before starting." },
      { q: "Can you colour match my car's paint?", a: "Yes, we use professional colour matching systems to ensure the repair blends seamlessly with your existing paintwork." },
      { q: "How long does bodywork repair take?", a: "Minor repairs can often be done same-day. Larger jobs may take 2–3 days depending on parts and paint drying times." },
      { q: "Do you handle insurance claims?", a: "We can provide detailed assessments and quotes to support your insurance claim. Speak to us for more details." },
    ],
  },
];

const GOOGLE_REVIEWS_URL = "https://share.google/ex6hlV5y3QnHwzg2P";

export const reviews: ReviewConfig[] = [
  { name: "James P.", text: "Took my car in for an MOT expecting the worst. They were straight with me about what needed doing — passed with a couple of advisories. Fair price too.", rating: 5, service: "MOT", source: "Google", sourceUrl: GOOGLE_REVIEWS_URL, pages: ["mot", "home", "areas", "services"] },
  { name: "Sarah M.", text: "Really impressed. They explained everything clearly and didn't try to push things I didn't need. Car's running well. Proper local garage.", rating: 5, service: "Servicing", source: "Google", sourceUrl: GOOGLE_REVIEWS_URL, pages: ["servicing", "home", "areas", "services"] },
  { name: "David R.", text: "Had a warning light another garage couldn't sort. Woodseer found the issue quickly and fixed it the same day. Much cheaper than the dealer too.", rating: 5, service: "Diagnostics", source: "Google", sourceUrl: GOOGLE_REVIEWS_URL, pages: ["diagnostics", "home", "areas", "services"] },
  { name: "Priya K.", text: "Brakes were making a horrible noise. Got booked in next morning and had new pads and discs fitted by lunchtime. Kept me updated throughout.", rating: 5, service: "Mechanical Repairs", source: "Google", sourceUrl: GOOGLE_REVIEWS_URL, pages: ["mechanical-repairs", "home"] },
  { name: "Tom H.", text: "Someone scraped my bumper in a car park. They repaired it and matched the paint well — you'd barely notice. Pleased with the result.", rating: 4, service: "Bodywork", source: "Google", sourceUrl: GOOGLE_REVIEWS_URL, pages: ["bodywork", "home"] },
  { name: "Aisha B.", text: "Been coming here for a couple of years now. Reliable, honest and they actually remember you. Would recommend to anyone nearby.", rating: 5, service: "General", source: "Google", sourceUrl: GOOGLE_REVIEWS_URL, pages: ["all"] },
  { name: "Mark L.", text: "Needed a clutch replacement. They quoted me less than two other garages and did a solid job. Car feels great now.", rating: 5, service: "Mechanical Repairs", source: "Google", sourceUrl: GOOGLE_REVIEWS_URL, pages: ["mechanical-repairs", "areas"] },
  { name: "Emma W.", text: "Quick MOT and they pointed out a few things to keep an eye on without charging extra. Honest service.", rating: 5, service: "MOT", source: "Google", sourceUrl: GOOGLE_REVIEWS_URL, pages: ["mot", "areas"] },
];

/** Filter reviews by page slug. Reviews without `pages`, or containing "all", appear everywhere. */
export function filterReviewsForPage(list: ReviewConfig[], page: string): ReviewConfig[] {
  return list.filter((r) => !r.pages || r.pages.length === 0 || r.pages.includes("all") || r.pages.includes(page));
}

export const areas: AreaConfig[] = [
  { slug: "poplar", name: "Poplar", description: "Serving drivers in Poplar and the surrounding streets with MOTs, servicing, diagnostics and repairs. We're just minutes away from Poplar High Street." },
  { slug: "canary-wharf", name: "Canary Wharf", description: "Convenient garage for Canary Wharf workers and residents. Drop your car off before work and collect it after — we're a short drive from the estate." },
  { slug: "isle-of-dogs", name: "Isle of Dogs", description: "Local garage for Isle of Dogs residents. Whether you need an MOT, a service or a repair, we're nearby and ready to help." },
  { slug: "limehouse", name: "Limehouse", description: "Trusted by Limehouse drivers for reliable, fairly-priced MOTs, servicing and repairs. Easy to reach from Commercial Road and the surrounding area." },
  { slug: "docklands", name: "Docklands", description: "Serving the wider Docklands area with full garage services. Professional diagnostics, mechanical repairs, bodywork and MOT testing." },
  { slug: "tower-hamlets", name: "Tower Hamlets", description: "Your local independent garage in Tower Hamlets. We've been keeping local vehicles on the road with honest, quality work." },
  { slug: "whitechapel", name: "Whitechapel", description: "Independent garage close to Whitechapel — MOT testing, servicing, diagnostics and repairs for E1 drivers. Easy to reach via Whitechapel Road and Commercial Road." },
  { slug: "brick-lane", name: "Brick Lane", description: "Trusted garage near Brick Lane (E1/E2). MOTs, servicing and bodywork without the dealer prices. A short drive from Bethnal Green Road." },
  { slug: "bow", name: "Bow", description: "Reliable independent garage for Bow drivers (E3). MOT testing, servicing, diagnostics and mechanical repairs — straight talking, fair prices." },
  { slug: "mile-end", name: "Mile End", description: "Local garage close to Mile End. We handle MOTs, servicing and brake work for drivers along Mile End Road, Burdett Road and Grove Road." },
  { slug: "bethnal-green", name: "Bethnal Green", description: "Independent garage for Bethnal Green (E2). MOT, servicing, diagnostics and repairs — clear quotes before any work starts." },
  { slug: "wapping", name: "Wapping", description: "Trusted local garage close to Wapping (E1W). MOT testing, servicing and bodywork repairs without long trips out of the area." },
  { slug: "stratford", name: "Stratford", description: "Honest, independent garage convenient for Stratford drivers (E15/E20). MOTs, servicing, diagnostics and repairs done properly." },
  { slug: "stepney", name: "Stepney", description: "Friendly local garage for Stepney (E1). Straightforward MOTs, servicing, diagnostics and repairs from a DVSA-authorised MOT station nearby." },
  { slug: "shadwell", name: "Shadwell", description: "Independent garage close to Shadwell (E1). MOT testing, servicing and repairs — a short drive via The Highway or Commercial Road." },
  { slug: "aldgate", name: "Aldgate", description: "Trusted local garage near Aldgate (EC3/E1). Drop off before work — MOTs, servicing and diagnostics handled with clear communication." },
  { slug: "canning-town", name: "Canning Town", description: "Independent garage convenient for Canning Town (E16). MOT, servicing, diagnostics and bodywork without the wait of a dealer." },
  { slug: "blackwall", name: "Blackwall", description: "Local garage close to Blackwall (E14). DVSA-authorised MOT station, full servicing, diagnostics and repairs — minutes from the Blackwall Tunnel approach." },
];

// Helper to get config with localStorage overrides
export function getBusinessConfig(): BusinessConfig {
  // Runtime stability: public SSR must match the first client render. Older
  // browser-only overrides caused NAP hydration mismatches, so public pages now
  // use the verified defaults here; server-backed admin config still powers the
  // compliance/schema blocks.
  return defaultBusiness;
}

export function getReviews(): ReviewConfig[] {
  return reviews;
}
